import WarpElement from '@warp-ds/elements-core';
import { css, html } from 'lit';
import throttle from 'lodash/throttle.js';

// To reduce bundle size, import only the components and icons you need
// https://github.com/warp-ds/elements?tab=readme-ov-file#import-components
// https://warp-ds.github.io/tech-docs/components/icons/
import { i18n } from '@lingui/core';
import {
	AdvertisingSlotComponent,
	AdvertisingNativeSlotComponent,
	PlacementId,
} from '@schibsted-nmp/advertising-companion';
import '@warp-ds/elements/components/badge';
import '@warp-ds/elements/components/button';
import '@warp-ds/elements/components/card';
import '@warp-ds/components/icon';
import { customElement } from 'lit/decorators/custom-element.js';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
	DisplayAdsPlacement,
	RecommendationItem,
	Recommendations,
	CrossBrandItem,
	AdvertisingAd,
	Item,
} from '../../server/types.js';
import './favorite-heart.js';
import './recommendation-badge.js';
import './recommendation-card.js';
import './cross-brand-card.js';
import './ab-test-recommendation-card/recommendation-card-price-first.js';
import './ab-test-recommendation-card/recommendation-card-title-first-badge-outside.js';
import './ab-test-recommendation-card/base-card.js';
import './no-history.js';
import {
	getDisplayAdsPlacement,
	isItemAdvertising,
	trackViewEvent,
	shouldTriggerInfiniteScroll,
	insertDisplayAds,
	getItemId,
} from './utils/index.js';

@customElement('frontpage-recommendations')
export class FrontpageRecommendations extends WarpElement {
	static styles = [
		...WarpElement.styles, // TODO: why doesn't pick up correct brand colours?
		css`
			.custom-label-style {
				background: linear-gradient(
					transparent,
					rgba(0, 0, 0, 0.04935) 13.71%,
					/* ... (rest of the gradient styles) */ rgba(0, 0, 0, 0.78731) 89.73%,
					rgba(0, 0, 0, 0.8)
				);
				width: 100%;
			}
			.two-lines {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
				overflow: hidden;
			}
				
			/** class added by advt-companion when collapsing **/
			.advt-ad-collapsed {
				display: none;
			}

			/** TODO: Remove this hard-coded style for favorite-heart once favorite-heart is rewritten **/
			/* layer: preflights */

			*,
			::before,
			::after {
				--w-rotate: 0;
				--w-rotate-x: 0;
				--w-rotate-y: 0;
				--w-rotate-z: 0;
				--w-scale-x: 1;
				--w-scale-y: 1;
				--w-scale-z: 1;
				--w-skew-x: 0;
				--w-skew-y: 0;
				--w-translate-x: 0;
				--w-translate-y: 0;
				--w-translate-z: 0;
			}

			/* layer: default */

			.rounded-l-8 {
				border-top-left-radius: 8px;
				border-bottom-left-radius: 8px;
			}

			.flex-row {
				flex-direction: row;
			}

			.flex-row-reverse {
				flex-direction: row-reverse;
			}

			.gap-16 {
				gap: 1.6rem;
			}

			.col-span-5 {
				grid-column: span 5 / span 5;
			}

			.line-clamp-2 {
				overflow: hidden;
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
				line-clamp: 2;
			}

			.outline {
				outline-style: solid;
			}

			.s-icon-subtle {
				color: var(--w-s-color-icon-subtle);
			}

			.mb-16 {
				margin-bottom: 1.6rem;
			}

			.mb-8 {
				margin-bottom: 0.8rem;
			}

			.ml-14 {
				margin-left: 1.4rem;
			}

			.mr-12 {
				margin-right: 1.2rem;
			}

			.mt-24 {
				margin-top: 2.4rem;
			}

			.p-24 {
				padding: 2.4rem;
			}

			.pb-24 {
				padding-bottom: 2.4rem;
			}

			.pt-32 {
				padding-top: 3.2rem;
			}

			.object-cover {
				object-fit: cover;
			}

			.text-ellipsis {
				text-overflow: ellipsis;
			}

			.whitespace-nowrap {
				white-space: nowrap;
			}

			@media (max-width: 479.9px) {
				.lt-sm\\:min-w-full {
					min-width: 100%;
				}
			}
			@media (min-width: 480px) {
				.sm\\:col-span-2 {
					grid-column: span 2 / span 2;
				}

				.sm\\:col-span-3 {
					grid-column: span 3 / span 3;
				}
			}
			@media (min-width: 768px) {
				.md\\:grid-cols-2 {
					grid-template-columns: repeat(2, minmax(0, 1fr));
				}

				.md\\:grid-cols-3 {
					grid-template-columns: repeat(3, minmax(0, 1fr));
				}
			}

			/** The line below is where the injected CSS goes, removing it means you get no CSS from the design system **/
			*,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.gap-y-32{row-gap:3.2rem}.grid-flow-row{grid-auto-flow:row}.grid-cols-\[repeat\(auto-fit\,_minmax\(15rem\,_1fr\)\)\]{grid-template-columns:repeat(auto-fit,minmax(15rem,1fr))}.static{position:static}.my-16{margin-top:1.6rem;margin-bottom:1.6rem}.mb-16{margin-bottom:1.6rem}.mb-4{margin-bottom:.4rem}.text-14{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}.text-ml{font-size:var(--w-font-size-ml);line-height:var(--w-line-height-ml)}@media (width>=768px){.md\:flex-row{flex-direction:row}.md\:grid-cols-\[repeat\(auto-fit\,_minmax\(22rem\,_1fr\)\)\]{grid-template-columns:repeat(auto-fit,minmax(22rem,1fr))}.md\:items-center{align-items:center}.md\:px-8{padding-left:.8rem;padding-right:.8rem}};
		`,
	];

	data!: Recommendations;
	newItems!: RecommendationItem[];
	url!: string;
	imgCdnUrl!: string;
	favoriteBaseUrl!: string;
	brand!: string;
	loginId!: string;
	nextPage = 0;
	canFetchMore = true;
	hasBottomReachedBeenTracked = false;
	isFetching = false;
	_displayAdsPlacement!: DisplayAdsPlacement[];
	variant!: string;
	fromConsentFlow = 'false';

	private _recommendations: Recommendations = {
		items: [],
		fetchMore: [],
		isPersonal: false,
		size: 0,
	};

	static properties = {
		data: { type: Object, default: {} },
		imgCdnUrl: { type: String },
		favoriteBaseUrl: { type: String },
		newItems: { type: Object, default: [], attribute: false },
		url: { type: String },
		brand: { type: String },
		loginId: { type: String },
		canFetchMore: { type: Boolean, attribute: false, state: true },
		nextPage: { type: Number, attribute: false, default: 0 },
		_displayAdsPlacement: { type: Object, default: [], attribute: false },
		locale: { type: String },
		variant: { type: String, default: 'control' },
		fromConsentFlow: { type: String },
	};

	private mountedAds = new Set<string>();
	private pendingAds: AdvertisingAd[] = [];

	get halfWayIndex() {
		return Math.ceil(this.data?.fetchMore.length / 2);
	}

	connectedCallback() {
		super.connectedCallback();
		const locale = this.locale || 'en';
		const messages = JSON.parse(this.getAttribute('translations') || '{}');

		i18n.load(locale, messages);
		i18n.activate(locale);

		// Add infinite scroll event listener
		this.addInfiniteScroll();

		// Track bottom reach
		window.addEventListener('scroll', this.handleBottomReach);
	}

	async firstUpdated() {
		this._recommendations = this.data;

		if (this.fromConsentFlow !== 'true') {
			this._displayAdsPlacement = getDisplayAdsPlacement();
		} else {
			this._displayAdsPlacement = [];
		}

		this.trackVisibleRecommendations();
	}

	trackVisibleRecommendations() {
		const getElementForItem = (item: RecommendationItem | CrossBrandItem): Element | null => {
			if (!item) return null;
			const id = isItemAdvertising(item) ? getItemId(item).replace(/:/g, '-') : getItemId(item);
			return this.shadowRoot?.getElementById(id) ?? null;
		};
		trackViewEvent(this.data, getElementForItem);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeInfiniteScroll();
		window.removeEventListener('scroll', this.handleBottomReach);
	}

	// Infinite scroll & fetch more
	addInfiniteScroll = () => {
		window.addEventListener('scroll', this.handleInfiniteScroll);
	};

	handleInfiniteScroll = throttle(() => {
		const target = this.shadowRoot?.getElementById('recommendations') || document.body;
		const containerHeight = target?.offsetHeight;
		const scrollPosition = window.innerHeight + window.scrollY;

		const { shouldFetchMore, shouldRemoveScroll } = shouldTriggerInfiniteScroll(
			scrollPosition,
			containerHeight,
			this.canFetchMore,
			this.nextPage,
			this.halfWayIndex,
		);

		if (shouldFetchMore) {
			this.fetchMore();
		}

		if (shouldRemoveScroll) {
			this.removeInfiniteScroll();
		}
	}, 200);

	removeInfiniteScroll = () => {
		window.removeEventListener('scroll', this.handleInfiniteScroll);
	};

	async fetchData(url: string) {
		const maxRetries: number = 3;
		let attempts = 0;

		while (attempts < maxRetries) {
			try {
				const response = await fetch(url);
				if (response.ok) return await response.json();
				console.warn(`Attempt ${attempts + 1}: Failed to fetch recommendations, resp:`, response);
			} catch (error) {
				console.error(`Attempt ${attempts + 1}: Error fetching data from ${url}:`, error);
			}
			attempts++;
			if (attempts < maxRetries) {
				await new Promise((resolve) => setTimeout(resolve, 1000)); // 1-second delay
			}
		}

		return null;
	}

	async fetchMore() {
		if (this.isFetching || !this.canFetchMore) return;

		this.isFetching = true;

		const url = `${this.url}/api/fetch-more/${this.data?.fetchMore[this.nextPage]}`;
		const data = await this.fetchData(url);

		if (this._displayAdsPlacement.length === 0) {
			this._displayAdsPlacement = getDisplayAdsPlacement();
		}

		if (data) {
			if (this.nextPage + 1 > this.halfWayIndex) {
				this.addInfiniteScroll();
			}
			this.nextPage = this.nextPage + 1;
			this.canFetchMore = this.nextPage !== this.data?.fetchMore.length;
			this.newItems = data.items;
			this.data.items.push(...this.newItems);
			insertDisplayAds(this._displayAdsPlacement, this.data);
			this.isFetching = false;
		} else {
			this.isFetching = false;
			this.removeInfiniteScroll();
		}
	}

	_handleFetchMore() {
		this.fetchMore();
	}

	handleBottomReach = () => {
		const bottom = document.querySelector('frontpage-recommendations')?.shadowRoot?.getElementById('bottom');
		if (bottom) {
			const reached = bottom.getBoundingClientRect().top <= window.innerHeight;

			if (reached && !this.canFetchMore && !this.hasBottomReachedBeenTracked) {
				this.hasBottomReachedBeenTracked = true;
				fetch(`${this.url}/tracking`);
			}
		}
	};

	/** --- Internal UI logic helpers --- **/
	showHistoryLink = () => {
		return this.data?.items.length > 0 && this.brand?.toUpperCase() === 'FINN' && this.data?.isPersonal;
	};
	showFetchMoreButton = () => {
		const isHalfway = this.nextPage === this.halfWayIndex;
		const hasMoreData = Array.isArray(this.data?.fetchMore) && this.data.fetchMore.length > 0;
		return hasMoreData && this.canFetchMore && isHalfway;
	};

	recommendationsTitle = () => {
		return this.data?.isPersonal
			? i18n.t({
					id: 'frontpage.recommendations.section.title',
					message: 'Personalized recommendations',
				})
			: i18n.t({
					id: 'frontpage.popular.section.title',
					message: 'Popular ads',
				});
	};

	crossBrandItem = (item: Item) => {
		if (item.type === 'CROSSBRAND') {
			return html`<cross-brand-card
					.item="${item}"
					.data="${this.data}"
					.locale="${this.locale}"
					id=${getItemId(item)}
				></cross-brand-card>`;
		}

		const divId = getItemId(item).replace(/:/g, '-');
		if (!this.mountedAds.has(divId)) {
			this.pendingAds.push({ adId: divId, adType: item.type });
		}

		return html`<div id="${divId}"></div>`;
	};

	protected async updated(changedProperties: Map<string, unknown>): Promise<void> {
		super.updated(changedProperties);

		await this.updateComplete;
		this.trackVisibleRecommendations();

		// Mount any pending ads
		for (const pendingAd of this.pendingAds) {
			const adId = pendingAd.adId;
			const advertisingAdRef = this.shadowRoot?.querySelector(`#${CSS.escape(adId)}`);

			if (advertisingAdRef) {
				const item = this.data?.items.find((item) => getItemId(item).replace(/:/g, '-') === adId);
				if (!item) continue;

				const itemId = adId as PlacementId;
				const root = createRoot(advertisingAdRef);
				const content = (item as RecommendationItem).content;
				const categories = {
					main_category: content?.main_category,
					sub_category: content?.sub_category,
					prod_category: content?.prod_category,
				};

				// Render ad using the correct ad component
				if (pendingAd.adType === 'NATIVE') {
					root.render(
						<AdvertisingNativeSlotComponent
							itemId={itemId}
							categories={JSON.stringify(categories)}
							initialLayoutType={'grid'}
							webComponentName={'frontpage-recommendations'}
						/>,
					);
				} else {
					root.render(
						<AdvertisingSlotComponent
							itemId={itemId}
							categories={JSON.stringify(categories)}
							initialLayoutType={'grid'}
							webComponentName={'frontpage-recommendations'}
						/>,
					);
				}

				this.mountedAds.add(adId);
				const adIndex = this.pendingAds.findIndex((ad) => ad.adId === adId);
				this.pendingAds.splice(adIndex);
			}
		}
	}

	renderVariantItem(item: RecommendationItem, index: number) {
		if (!item || !item.heading || !item.image || !item.url) return html``;
		switch (this.variant) {
			case 'control':
				return html`<recommendation-card
					.item=${item}
					.data=${this.data}
					.imgCdnUrl=${this.imgCdnUrl}
					.favoriteBaseUrl=${this.favoriteBaseUrl}
					.loginId=${this.loginId}
					.locale=${this.locale}
					.index=${index}
					id=${getItemId(item)}
				></recommendation-card>`;
			case 'priceFirst':
				return html`<recommendation-card-price-first
					.item=${item}
					.data=${this.data}
					.imgCdnUrl=${this.imgCdnUrl}
					.favoriteBaseUrl=${this.favoriteBaseUrl}
					.loginId=${this.loginId}
					.locale=${this.locale}
					id=${getItemId(item)}
				></recommendation-card-price-first>`;
			case 'titleFirst':
				return html`<recommendation-card-title-first
					.item=${item}
					.data=${this.data}
					.imgCdnUrl=${this.imgCdnUrl}
					.favoriteBaseUrl=${this.favoriteBaseUrl}
					.loginId=${this.loginId}
					.locale=${this.locale}
					id=${getItemId(item)}
				></recommendation-card-title-first>`;
			case 'titleFirstBadgeOutside':
				return html`<recommendation-card-title-first-badge-outside
					.item=${item}
					.data=${this.data}
					.imgCdnUrl=${this.imgCdnUrl}
					.favoriteBaseUrl=${this.favoriteBaseUrl}
					.loginId=${this.loginId}
					.locale=${this.locale}
					.index=${index}
					id=${getItemId(item)}
				></recommendation-card-title-first-badge-outside>`;
			default:
				return html`<recommendation-card
					.item=${item}
					.data=${this.data}
					.imgCdnUrl=${this.imgCdnUrl}
					.favoriteBaseUrl=${this.favoriteBaseUrl}
					.loginId=${this.loginId}
					.locale=${this.locale}
					.index=${index}
					id=${getItemId(item)}
				></recommendation-card>`;
		}
	}

	render() {
		// If we have recommendations, show them
		return this._recommendations?.size > 0
			? html`
					<div id="recommendations">
						<section>
							<div class="flex flex-col items-start md:flex-row md:items-center my-16">
								<h2 class="text-ml mb-4">${this.recommendationsTitle()}</h2>
								${this.showHistoryLink()
									? html`<a href="/anbefalinger/historikk" class="link text-14 md:px-8">
											${i18n.t({
												id: 'frontpage.recommendations.history.link.text',
												message: 'Why we recommend these ads?',
											})}
										</a>`
									: ''}
							</div>
							<div
								class="grid grid-cols-[repeat(auto-fit,_minmax(15rem,_1fr))] md:grid-cols-[repeat(auto-fit,_minmax(22rem,_1fr))] gap-x-16 ${this
									.variant === 'control' || this.variant === 'default'
									? `gap-y-16`
									: `gap-y-32`} grid-flow-row max-w-full mb-16"
							>
								${this.data?.items?.map((item, index) =>
									isItemAdvertising(item)
										? this.crossBrandItem(item)
										: this.renderVariantItem(item as RecommendationItem, index),
								)}
							</div>
							${this.showFetchMoreButton()
								? html`
										<div class="text-center mb-16">
											<w-button variant="primary" @click="${this._handleFetchMore}"
												>${i18n.t({
													id: 'frontpage.recommendations.fetch-more-button.label',
													message: 'See more recommendations',
												})}</w-button
											>
										</div>
									`
								: ''}
							<span class="hidden" id="bottom"></span>
						</section>
					</div>
				`
			: html``;
	}
}
